/**
 * HelpButton: the "?" that opens a module's visual guide in HelpPanel; HelpTitle: a page's (or a section's) title
 * with its "?" right after it. One "?" per page or module, after the title, never inside a group of action buttons.
 *
 *   import { HelpTitle } from "../../components/Help/HelpButton";
 *   import BankGuide from "../../components/Help/guides/BankGuide";
 *
 *   <HelpTitle helpId="bank" helpTitle={t("help.bank.title")} guide={<BankGuide />}>
 *     {t("nav.question_bank")}
 *   </HelpTitle>
 *
 * HelpTitle props:
 * - children: the heading's own text (the existing title key of the page).
 * - helpId (required), helpTitle (required), guide (the guide element): passed on to HelpButton as id, title and
 *   children.
 * - as: the heading tag, "h2" (default; the page titles are h2 under the app's header) or "h3" for a section.
 * - size: "page" (default: text-2xl, the pages' titles) or "section" (text-lg). It sets the heading's look and
 *   the "?"'s negative margins, so the row keeps the heading's own height (the "?" is 36px, 44px on phones).
 * - className: added to the heading (e.g. "truncate"); rowClassName: added to the row (a flex row, gap-1.5 so the
 *   focus ring of the "?" clears the last letter).
 *
 * HelpButton, alone (e.g. after a title HelpTitle cannot draw):
 *
 *   import HelpButton from "../../components/Help/HelpButton";
 *   <HelpButton id="bank" title={t("help.bank.title")}><BankGuide /></HelpButton>
 *
 * - id (required): the guide's name, e.g. "bank", "matrix", the same as its i18n branch help.<id>. It names the test
 *   ids: help-button-<id> (with data-seen="true|false"), help-link-<id>, help-panel-<id>, help-body-<id>.
 * - title (required): the guide's title, the panel's heading (help.<id>.title). The "?"'s aria-label and tooltip
 *   are t("help.common.open_named", { title }).
 * - children: the guide (a component of HelpKit blocks); it is mounted only while the panel is open.
 * - variant: "icon" (default) is the round "?" (36px, 44px on phones), with a small blue dot until this user has
 *   opened this guide once; "link" is an inline text link with a book icon, e.g. in an empty state.
 * - label: the link's text (variant "link"); default t("help.common.how_it_works").
 * - className: added to the button (e.g. a margin).
 * The "seen" mark is kept per user and guide (localStorage "help-seen:<user uuid>:<id>", in memory where storage is
 * blocked); two HelpButtons with the same id (the "?" and a link in an empty state) share it. useHelpSeen(id) reads
 * it (re-rendering when it changes, in other tabs too) and markHelpSeen(id, user) sets it.
 */
import React, { useContext, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import HelpPanel from "./HelpPanel";
import { UserContext } from "../../utils/contexts/UserContext";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

// "Seen" marks: in localStorage when it can be used (private windows and blocked storage throw), and in memory for
// this visit in any case. Per user: teachers share school computers.
const PREFIX = "help-seen:";
const seenInMemory = new Set();
const listeners = new Set();
const storageKey = (id, user) => `${PREFIX}${user?.uuid || "anon"}:${id}`;

function isHelpSeen(key) {
  if (seenInMemory.has(key)) return true;
  try {
    return window.localStorage.getItem(key) !== null;
  } catch (e) {
    return false;
  }
}

export function markHelpSeen(id, user) {
  const key = storageKey(id, user);
  seenInMemory.add(key);
  try {
    window.localStorage.setItem(key, "1");
  } catch (e) {
    // Storage unavailable: the mark lasts for this visit only.
  }
  listeners.forEach((listener) => listener());
}

const subscribe = (listener) => {
  listeners.add(listener);
  // Another tab opened a guide (or the storage was cleared there).
  const onStorage = (event) => {
    if (!event.key || event.key.startsWith(PREFIX)) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

// Whether the signed-in user has opened the guide `id`.
export function useHelpSeen(id) {
  const { user } = useContext(UserContext) || {};
  const key = storageKey(id, user);
  return useSyncExternalStore(subscribe, () => isHelpSeen(key), () => true);
}

export default function HelpButton({ id, title, children, variant = "icon", label, className = "" }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  const [open, setOpen] = useState(false);
  const seen = useHelpSeen(id);
  const name = t("help.common.open_named", { title });

  const openPanel = () => {
    setOpen(true);
    if (!seen) markHelpSeen(id, user);
  };

  return (
    <>
      {variant === "link" ? (
        <button
          type="button"
          aria-haspopup="dialog"
          onClick={openPanel}
          data-testid={`help-link-${id}`}
          className={`inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg px-1.5 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 md:min-h-[2.25rem] ${focusRing} ${className}`}
        >
          <BookOpenIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {label || t("help.common.how_it_works")}
        </button>
      ) : (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={name}
          title={name}
          onClick={openPanel}
          data-testid={`help-button-${id}`}
          data-seen={seen ? "true" : "false"}
          className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-700 md:h-9 md:w-9 ${focusRing} ${className}`}
        >
          <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
          {!seen && (
            <span
              className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white md:right-1 md:top-1"
              aria-hidden="true"
            />
          )}
        </button>
      )}
      <HelpPanel open={open} onClose={() => setOpen(false)} title={title} id={id}>
        {children}
      </HelpPanel>
    </>
  );
}

// The heading's look and the "?"'s margins that keep the row at the heading's line height (32px for text-2xl,
// 28px for text-lg) with a 44px "?" on phones and a 36px one from md up.
const TITLE_SIZES = {
  page: { heading: "text-2xl font-semibold tracking-tight text-slate-900", button: "-my-1.5 md:-my-0.5" },
  section: { heading: "text-lg font-semibold text-slate-900", button: "-my-2 md:-my-1" },
};

export function HelpTitle({ helpId, helpTitle, guide, children, as: Heading = "h2", size = "page", className = "", rowClassName = "" }) {
  const look = TITLE_SIZES[size] || TITLE_SIZES.page;
  return (
    <div className={`flex min-w-0 items-center gap-1.5 ${rowClassName}`}>
      <Heading className={`min-w-0 ${look.heading} ${className}`}>{children}</Heading>
      <HelpButton id={helpId} title={helpTitle} className={look.button}>
        {guide}
      </HelpButton>
    </div>
  );
}
